import type { AuthUser, ProfileModule } from '../services/authService';

/**
 * Build a lookup Set of "moduleCode::actionCode" keys for all ACTIVE actions
 * across the given modules. Used for O(1) permission checks.
 */
export function buildActionLookupSet(modules: ProfileModule[]): Set<string> {
    const set = new Set<string>();
    for (const mod of modules) {
        for (const action of mod.actions) {
            if (action.is_active) {
                const code = action.action_code ?? action.code;
                if (code) set.add(`${mod.code}::${code}`);
            }
        }
    }
    return set;
}

/**
 * Check if a user can perform a specific action on a module.
 * Mirrors the backend PermissionService::canPerformAction truth table.
 *
 * Rules (in order):
 *  1. is_admin === true → always true
 *  2. No profile → false
 *  3. Module not found or not active → false
 *  4. Module has 0 actions → true (backward-compat R6 fallback)
 *  5. Action found and active → true
 *  6. Action not found or inactive → false
 */
export function canPerformAction(
    user: Pick<AuthUser, 'is_admin' | 'profile'>,
    moduleCode: string,
    actionCode: string,
): boolean {
    // 1. Admin bypass
    if (user.is_admin) return true;

    // 2. No profile
    if (!user.profile) return false;

    // 3. Module not found or not active
    const mod = user.profile.modules.find((m) => m.code === moduleCode);
    // Login payload omits is_active on modules already filtered as active.
    if (!mod || mod.is_active === false) return false;

    // 4. Empty actions → backward-compat fallback (R6)
    if (mod.actions.length === 0) return true;

    // 5-6. Check for active action
    return mod.actions.some((a) => {
        const code = a.action_code ?? a.code;
        return code === actionCode && a.is_active;
    });
}
